import { useEffect, useRef, useState } from "react";
import { Panel, Typography } from "@/components/atoms";
import { useOperator, useSubscriptionAssignment } from "@/hooks";
import { useGetProviderPaymentMethodsQuery } from "@/redux/apis/subscription/subscriptionPlansApi";
import { ChangePaymentMethodModal } from "../modals/ChangePaymentMethodModal";

const capitalizeFirstLetter = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

export const PaymentInformation = () => {
  const operator = useOperator();
  const { data: paymentMethods } = useGetProviderPaymentMethodsQuery({ providerId: operator.id });
  const [isChangePaymentMethodOpen, setIsChangePaymentMethodOpen] = useState(false);
  const [shouldShowAddCard, setShouldShowAddCard] = useState(false);
  const { subscriptionStatus, isLoading: isLoadingSubscription } = useSubscriptionAssignment();
  const hasAutoOpened = useRef(false);

  // Auto-open the modal with add card view for existing users (no payment method yet).
  // Uses a ref so it only fires once per mount — closing the modal won't re-trigger it,
  // but navigating away and back will (component remounts, ref resets).
  useEffect(() => {
    if (!isLoadingSubscription && subscriptionStatus.userType === "existing" && !hasAutoOpened.current) {
      hasAutoOpened.current = true;
      setShouldShowAddCard(true);
      setIsChangePaymentMethodOpen(true);
    }
  }, [isLoadingSubscription, subscriptionStatus.userType]);

  // Find the default payment method
  const defaultPaymentMethod = paymentMethods?.find((pm) => pm.isDefault) || paymentMethods?.[0];

  // Format card display text
  const cardDisplayText = defaultPaymentMethod
    ? `${capitalizeFirstLetter(defaultPaymentMethod.card.brand)} ending in ${defaultPaymentMethod.card.last4}`
    : "-";

  return (
    <>
      <Panel className="flex flex-col gap-4">
        <Typography variant="h2">Payment Information</Typography>
        <div className="flex flex-col">
          {/* Payment method */}
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-2">
              <Typography variant="paragraph" className="min-w-[140px] text-neutral-dark-gray">
                Payment method
              </Typography>
              <Typography variant="paragraph" className="text-neutral-dark-gray">
                :
              </Typography>
              <span className="ml-2 text-neutral-dark-gray">💳</span>
              <Typography variant="paragraph" className="ml-2">
                {cardDisplayText}
              </Typography>
            </div>
            <Typography variant="action" className="cursor-pointer text-info" onClick={() => setIsChangePaymentMethodOpen(true)}>
              Edit method
            </Typography>
          </div>

          {/* TODO: Billing email and Billing to sections are temporarily hidden until we have a working API to edit and display them properly */}
        </div>
      </Panel>

      <ChangePaymentMethodModal
        open={isChangePaymentMethodOpen}
        onClose={() => {
          setIsChangePaymentMethodOpen(false);
          setShouldShowAddCard(false);
        }}
        initialShowAddCard={shouldShowAddCard}
      />
    </>
  );
};
